import { dispatchAppEvent } from '@/src/renderer/lib/events'
import type { LifeSpace } from '@/src/renderer/lib/db/models'

/**
 * Tiny module-scoped stash for "after the project tab opens, scroll the
 * project list to this task and select it". Used by the Reactivate
 * dropdown's "Im Projekt aufwecken" toast — the toast button calls
 * `setPendingTaskJump` and then `openTab` with the project's regular
 * path (no query string), so the tab manager can re-use the existing
 * tab if it's already open. ProjectPage consumes the stash on data-ready.
 *
 * Sync over async by design: `openTab` activates the tab synchronously
 * and ProjectPage's data-ready effect runs in a later tick, by which
 * time the value is already there.
 *
 * **Seit #210 in `lib/`**: Auch die Sidebar springt (Favoriten) — und die darf `features/` nicht
 * importieren. Dazu kam **Kurz notiert** als Ziel und das Ereignis `pending-task-jump`: Steht die
 * Seite schon offen, lief ihr Effekt nicht erneut, und der Sprung blieb liegen.
 */

interface PendingTaskJump {
  /** `project:<id>` oder `quick-notes:<Lebensbereich>`. */
  target: string
  taskId: string
  /**
   * Die Aufgabe wurde soeben **wieder geöffnet** („Wieder öffnen" auf **Geschafft**).
   *
   * Sie steht in Dexie in diesem Moment oft noch als erledigt — der Schreibvorgang
   * läuft nebenher. Ohne diesen Hinweis hielte die Projektseite sie für etwas, das
   * hinter dem **Geschafft-Schalter** liegt, und klappte ihn auf, nur damit die
   * Aufgabe einen Wimpernschlag später in die Liste rutscht.
   */
  reopened: boolean
  /** Nach dem Auswählen die **Aufgaben-Details** öffnen (Sprung aus „Favoriten“, #210). */
  openDetails: boolean
}

export interface TaskJumpOptions {
  reopened?: boolean
  openDetails?: boolean
}

export interface ConsumedTaskJump {
  taskId: string
  reopened: boolean
  openDetails: boolean
}

let pending: PendingTaskJump | null = null

function stash(target: string, taskId: string, options: TaskJumpOptions): void {
  pending = { target, taskId, reopened: options.reopened ?? false, openDetails: options.openDetails ?? false }
  dispatchAppEvent('pending-task-jump')
}

function take(target: string): ConsumedTaskJump | null {
  if (!pending || pending.target !== target) return null
  const { taskId, reopened, openDetails } = pending
  pending = null
  return { taskId, reopened, openDetails }
}

export function setPendingTaskJump(projectId: string, taskId: string, options: TaskJumpOptions = {}): void {
  stash(`project:${projectId}`, taskId, options)
}

/** Wie {@link setPendingTaskJump}, für eine Aufgabe ohne Projekt — Ziel ist **Kurz notiert**. */
export function setPendingQuickNotesJump(lifeSpace: LifeSpace, taskId: string, options: TaskJumpOptions = {}): void {
  stash(`quick-notes:${lifeSpace}`, taskId, options)
}

/**
 * Returns the pending jump if it targets `projectId` (and clears the
 * stash), otherwise null. Stash is consumed once — a second caller for
 * the same project gets null.
 */
/** Seiten, auf denen eine Aufgabe ohne Projekt stehen kann, wenn nicht in Kurz notiert (#210). */
export type TaskJumpPage = 'upcoming' | 'dormant' | 'recurring' | 'done'

export function setPendingPageJump(page: TaskJumpPage, lifeSpace: LifeSpace, taskId: string, options: TaskJumpOptions = {}): void {
  stash(`${page}:${lifeSpace}`, taskId, options)
}

export function consumePendingPageJump(page: TaskJumpPage, lifeSpace: LifeSpace): ConsumedTaskJump | null {
  return take(`${page}:${lifeSpace}`)
}

/**
 * Legt einen abgeholten Sprung zurück, der nicht mehr ausgeführt wird: Sein Effekt wurde abgebaut,
 * bevor die Seite fertig war — so im Entwicklungsmodus immer beim Einhängen (StrictMode lässt jeden
 * Effekt einmal auf- und gleich wieder abbauen; gemeldet 01.10.2026: der Sprung auf eine frisch
 * geöffnete Seite ging verloren). Liegt schon ein neuerer bereit, gewinnt der.
 */
export function putBackPageJump(page: TaskJumpPage, lifeSpace: LifeSpace, jump: ConsumedTaskJump): void {
  if (pending) return
  pending = { target: `${page}:${lifeSpace}`, taskId: jump.taskId, reopened: jump.reopened, openDetails: jump.openDetails }
}

export function consumePendingTaskJump(projectId: string): ConsumedTaskJump | null {
  return take(`project:${projectId}`)
}

export function consumePendingQuickNotesJump(lifeSpace: LifeSpace): ConsumedTaskJump | null {
  return take(`quick-notes:${lifeSpace}`)
}

/*
  **Sprung auf die Pinnwand** (Treffer der **Suche**, #233): Die Projektseite öffnet die Pinnwand,
  wählt das Element aus und fährt den Ausschnitt hin. Ein eigener Merker neben dem für Aufgaben —
  dieselbe Bauart, ein eigenes Ereignis, damit eine schon offene Seite ihn abholt.
*/
let pendingCanvas: { projectId: string; noteId: string } | null = null

export function setPendingCanvasJump(projectId: string, noteId: string): void {
  pendingCanvas = { projectId, noteId }
  dispatchAppEvent('pending-canvas-jump')
}

/** Die Kennung des Elements, wenn der Sprung diesem Projekt gilt — einmal; sonst `null`. */
export function consumePendingCanvasJump(projectId: string): string | null {
  if (!pendingCanvas || pendingCanvas.projectId !== projectId) return null
  const { noteId } = pendingCanvas
  pendingCanvas = null
  return noteId
}

/** Test-only: drops any pending jump without returning it. */
export function _resetPendingTaskJumpForTests(): void {
  pending = null
  pendingCanvas = null
}
